// AppRouter — react-router-dom v6 setup. Discover / Pulse / Pair all
// live under the AppLayout shell; /test/chart sits at the top level
// outside the layout so it renders bare (consistent with how it shipped
// before the router existed).

import { type ReactNode, Suspense, lazy, useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthCallbackPage } from '../components/auth/AuthCallback'
import { AuthContinuePage } from '../components/auth/AuthContinue'
import { ReferralCapture } from '../components/auth/ReferralCapture'
import { AppLayout } from '../components/layout/AppLayout'
import { loadPairPage, peekPairPage } from '../lib/pairPagePreload'
import { createPreloadedModule } from '../lib/preloadedModule'
import { NotFoundPage } from '../pages/NotFoundPage'

// Each page stays its own chunk (AppRouter.bundle.test.ts guards that), and
// each is ALSO reachable synchronously once warmed.
//
// lazy() alone is not enough and warming it does not help: React re-suspends a
// route on its first render even when the import already resolved, because the
// thenable handed to lazy() is new. Measured on t4b — the first visit to
// /pulse sat on the empty route fallback for 982ms. That blank frame is why
// these four were switched to static imports during the guest-shell work; it
// pulled every page into the entry bundle to buy it.
//
// peek() is the way to have both: render the resolved component directly when
// it is there, fall back to lazy() only on a genuinely cold chunk. Same shape
// the pair page has used all along.
const discoverModule = createPreloadedModule(() =>
  import('../pages/DiscoverPage').then((module) => module.DiscoverPage)
)
const pulseModule = createPreloadedModule(() =>
  import('../pages/PulsePage').then((module) => module.PulsePage)
)
const portfolioModule = createPreloadedModule(() =>
  import('../pages/PortfolioPage').then((module) => module.PortfolioPage)
)
const rewardsModule = createPreloadedModule(() =>
  import('../pages/RewardsPage').then((module) => module.RewardsPage)
)

const LazyDiscoverPage = lazy(() =>
  import('../pages/DiscoverPage').then((module) => ({ default: module.DiscoverPage }))
)
const LazyPulsePage = lazy(() =>
  import('../pages/PulsePage').then((module) => ({ default: module.PulsePage }))
)
const LazyPortfolioPage = lazy(() =>
  import('../pages/PortfolioPage').then((module) => ({ default: module.PortfolioPage }))
)
const LazyRewardsPage = lazy(() =>
  import('../pages/RewardsPage').then((module) => ({ default: module.RewardsPage }))
)

function DiscoverPage() {
  const Warm = discoverModule.peek()
  return Warm ? <Warm /> : <LazyDiscoverPage />
}
function PulsePage() {
  const Warm = pulseModule.peek()
  return Warm ? <Warm /> : <LazyPulsePage />
}
function PortfolioPage() {
  const Warm = portfolioModule.peek()
  return Warm ? <Warm /> : <LazyPortfolioPage />
}
function RewardsPage() {
  const Warm = rewardsModule.peek()
  return Warm ? <Warm /> : <LazyRewardsPage />
}

const LazyPairPage = lazy(() => loadPairPage().then((component) => ({ default: component })))

function PairPageRoute() {
  const PreloadedPairPage = peekPairPage()
  return PreloadedPairPage ? <PreloadedPairPage /> : <LazyPairPage />
}
// Test stands (fixtures, design rounds) ship only in builds made with
// VITE_WT_TEST_STANDS=1 (the preview). In production the flag is unset: Vite
// folds the test to false, the stand's chunk is never emitted, and its route
// answers 404 (security, stage 1: the stands were open to everyone). The
// test stays at each definition: a dynamic import passed into a helper would
// be kept by the bundler whatever the flag says (measured: it was).
const TEST_STANDS = import.meta.env.VITE_WT_TEST_STANDS === '1'

const TestChartPage = TEST_STANDS
  ? lazy(() => import('../pages/TestChart').then((module) => ({ default: module.TestChartPage })))
  : NotFoundPage
const TestUiPage = TEST_STANDS
  ? lazy(() => import('../pages/TestUi').then((module) => ({ default: module.TestUiPage })))
  : NotFoundPage
const TestMobilePage = TEST_STANDS
  ? lazy(() => import('../pages/TestMobile').then((module) => ({ default: module.TestMobilePage })))
  : NotFoundPage
const TestFiltersPage = TEST_STANDS
  ? lazy(() =>
      import('../pages/TestFilters').then((module) => ({ default: module.TestFiltersPage }))
    )
  : NotFoundPage
const TestDiscoverPage = TEST_STANDS
  ? lazy(() =>
      import('../pages/TestDiscover').then((module) => ({ default: module.TestDiscoverPage }))
    )
  : NotFoundPage
const TestMobileStylePage = TEST_STANDS
  ? lazy(() =>
      import('../pages/TestMobileStyle').then((module) => ({ default: module.TestMobileStylePage }))
    )
  : NotFoundPage
const TestMobilePairPage = TEST_STANDS
  ? lazy(() =>
      import('../pages/TestMobilePair').then((module) => ({ default: module.TestMobilePairPage }))
    )
  : NotFoundPage
const TestMobileHoldersPage = TEST_STANDS
  ? lazy(() =>
      import('../pages/TestMobileHolders').then((module) => ({
        default: module.TestMobileHoldersPage,
      }))
    )
  : NotFoundPage
const TokensNativePage = lazy(() =>
  import('../pages/TokensNativePage').then((module) => ({ default: module.TokensNativePage }))
)
const TestRewardsMobilePage = TEST_STANDS
  ? lazy(() =>
      import('../pages/TestRewardsMobile').then((module) => ({
        default: module.TestRewardsMobilePage,
      }))
    )
  : NotFoundPage
const TestRewardsConcept = TEST_STANDS
  ? lazy(() =>
      import('../pages/TestRewardsConcept').then((module) => ({
        default: module.TestRewardsConcept,
      }))
    )
  : NotFoundPage
const TestPickersPage = TEST_STANDS
  ? lazy(() =>
      import('../pages/TestPickers').then((module) => ({ default: module.TestPickersPage }))
    )
  : NotFoundPage
const TestMoneyPage = TEST_STANDS
  ? lazy(() => import('../pages/TestMoney').then((module) => ({ default: module.TestMoneyPage })))
  : NotFoundPage
const TestSoundsPage = lazy(() =>
  import('../pages/TestSounds').then((module) => ({ default: module.TestSoundsPage }))
)
const TestToastsPage = TEST_STANDS
  ? lazy(() => import('../pages/TestToasts').then((module) => ({ default: module.TestToastsPage })))
  : NotFoundPage
const TestPnlPage = TEST_STANDS
  ? lazy(() => import('../pages/TestPnl').then((module) => ({ default: module.TestPnlPage })))
  : NotFoundPage
const DesignLabPage = TEST_STANDS
  ? lazy(() =>
      import('../pages/design-lab/DesignLabPage').then((module) => ({
        default: module.DesignLabPage,
      }))
    )
  : NotFoundPage
const DesignLabV2Page = TEST_STANDS
  ? lazy(() => import('../pages/design-lab/V2Page').then((module) => ({ default: module.V2Page })))
  : NotFoundPage

function page(node: ReactNode) {
  return <Suspense fallback={<div aria-label="Loading page" />}>{node}</Suspense>
}

// Warm every routed page, not just the pair — peek() only helps once the
// module is actually in hand.
const warmRoutePages = () => {
  void loadPairPage().catch(() => undefined)
  void discoverModule.load().catch(() => undefined)
  void pulseModule.load().catch(() => undefined)
  void portfolioModule.load().catch(() => undefined)
  void rewardsModule.load().catch(() => undefined)
}

function PairPagePreload() {
  useEffect(() => {
    const preload = warmRoutePages
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(preload, { timeout: 2_000 })
      return () => window.cancelIdleCallback(id)
    }
    const id = setTimeout(preload, 500)
    return () => clearTimeout(id)
  }, [])
  return null
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <PairPagePreload />
      <Routes>
        <Route path="/" element={<Navigate to="/discover" replace />} />
        <Route path="/auth/google/callback" element={<AuthCallbackPage provider="google" />} />
        <Route path="/auth/telegram/callback" element={<AuthCallbackPage provider="telegram" />} />
        <Route path="/auth/continue" element={<AuthContinuePage />} />
        {/* Invite link: stores the referral code, then /discover. */}
        <Route path="/r/:code" element={<ReferralCapture />} />
        <Route element={<AppLayout />}>
          <Route path="/discover" element={page(<DiscoverPage />)} />
          <Route path="/pulse" element={page(<PulsePage />)} />
          <Route path="/portfolio" element={page(<PortfolioPage />)} />
          {/* Read-only view of ANY wallet. The userland read surfaces are
              public and address-scoped, so this needs no account. */}
          <Route path="/portfolio/:address" element={page(<PortfolioPage />)} />
          <Route path="/rewards" element={page(<RewardsPage />)} />
          {/* /test/rewards — the Rewards concept stand (2026-10-27): the tier
              card, every tier's material and the tickets, on fixture figures. */}
          {TEST_STANDS && <Route path="/test/rewards" element={page(<TestRewardsConcept />)} />}
          <Route path="/pair/:addr" element={page(<PairPageRoute />)} />
          {/* /tokens/native — unified realtime trade feed across every
              TON-paired pool + launchpad. x1000.finance/tokens/native
              parity: vertical list of trades, all sources, WS-first.
              Previous /pair/native aggregate aliasing dropped — feed
              is the more useful default per user request. */}
          <Route path="/tokens/native" element={page(<TokensNativePage />)} />
          <Route path="/:addr" element={page(<PairPageRoute />)} />
          <Route path="/:left/:right/:pool" element={page(<PairPageRoute />)} />
          <Route path="/:left/:right" element={page(<PairPageRoute />)} />
          {/* 404 inside the app's frame, header and all. Without the stands
              every /test/… and /design-lab/… path is one too, never a pair
              page (/:left/:right would catch it); the :segment forms outrank
              /:left/:right/:pool for deeper paths. */}
          {!TEST_STANDS && <Route path="/test/*" element={<NotFoundPage />} />}
          {!TEST_STANDS && <Route path="/test/:stand/*" element={<NotFoundPage />} />}
          {!TEST_STANDS && <Route path="/design-lab/*" element={<NotFoundPage />} />}
          {!TEST_STANDS && <Route path="/design-lab/:page/*" element={<NotFoundPage />} />}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        {/* /test/sounds — the listening round: every sound, A and B */}
        <Route path="/test/sounds" element={page(<TestSoundsPage />)} />
        {/* The stands exist only in a VITE_WT_TEST_STANDS=1 build. */}
        {TEST_STANDS && (
          <>
            <Route path="/test/chart" element={page(<TestChartPage />)} />
            <Route path="/test/ui" element={page(<TestUiPage />)} />
            {/* /test/mobile — лаборатория мобильной навигации (standalone):
                варианты таббара / мини-хедера / трейд-бара в рамках 390px. */}
            <Route path="/test/mobile" element={page(<TestMobilePage />)} />
            {/* /test/discover — конструктор мобильного Discover: страница в
                рамке 390px + панель настроек (пресеты/тумблеры/метрики). */}
            <Route path="/test/discover" element={page(<TestDiscoverPage />)} />
            {/* /test/filters — стенд панели фильтров Discover (standalone). */}
            <Route path="/test/filters" element={page(<TestFiltersPage />)} />
            {/* /test/pickers — the money windows' choice lists (From, the token,
                a wallet's tokens) on fixtures, and the marks for the one chosen. */}
            <Route path="/test/pickers" element={page(<TestPickersPage />)} />
            {/* /test/money — Portfolio's money windows (Exchange, Withdraw, Move
                funds, Orders and an order's details) on fixtures, measured where a
                guest cannot open them. */}
            <Route path="/test/money" element={page(<TestMoneyPage />)} />
            <Route path="/test/toasts" element={page(<TestToastsPage />)} />
            {/* /test/pnl — the PnL switch, its figures and the calendar on fixtures,
                and the GRAM / USD variant for Portfolio (2026-11-03) */}
            <Route path="/test/pnl" element={page(<TestPnlPage />)} />
            {/* /test/mobile-style — раунд пиксель-айдентики: дизеринг-текстуры,
                варианты хедера и таббара, полные миры 390px (рефы 2026-07-26). */}
            <Route path="/test/mobile-style" element={page(<TestMobileStylePage />)} />
            {/* /test/mobile-pair — мобильная страница токена (реф GMGN Chart&Act):
                шапка/метрика/стат-полоса/чарт/табы/трейд-бар, выбор глазами. */}
            <Route path="/test/mobile-pair" element={page(<TestMobilePairPage />)} />
            {/* /test/mobile-holders — лаба вкладки Holders на мок-строках (в проде
                честный empty state до on-chain holders index). */}
            <Route path="/test/mobile-holders" element={page(<TestMobileHoldersPage />)} />
            {/* /test/rewards-mobile — Rewards on the phone in Soft, on the desktop
                stand's fixtures, for review before it replaces the old screen. */}
            <Route path="/test/rewards-mobile" element={page(<TestRewardsMobilePage />)} />
            {/* /design-lab — playground системы компонентов (standalone, вне
                AppLayout): язык + семьи вариантов, мок-данные, dev-only. */}
            <Route path="/design-lab" element={page(<DesignLabPage />)} />
            {/* /design-lab/v2 — «мягкий терминал» по референс-доскам 2026-08-30:
                новая страница, старая лаба не тронута. */}
            <Route path="/design-lab/v2" element={page(<DesignLabV2Page />)} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  )
}
